import { useId } from 'react';
import { cn } from '@/lib/cn';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';
import './nexa-mark.css';

export type NexaMarkVariant = 'splash' | 'compact' | 'brand';

export interface NexaMarkProps {
  variant: NexaMarkVariant;
  className?: string;
  /** Override `prefers-reduced-motion` (stories / tests). */
  reducedMotion?: boolean | undefined;
}

export function NexaMark({ variant, className, reducedMotion }: NexaMarkProps) {
  const rawId = useId();
  const uid = rawId.replace(/:/g, '');
  const armGrad = `nexa-arm-${uid}`;
  const arrowGrad = `nexa-arrow-${uid}`;
  // The chrome lockup ('brand') never animates: a logo that pulses forever in the
  // header reads as a permanent loading state. Motion stays with splash/compact.
  const prefersStatic = usePrefersReducedMotion(reducedMotion);
  const staticMark = variant === 'brand' || prefersStatic;

  return (
    <svg
      viewBox="0 0 650 600"
      className={cn(
        'nexa-mark',
        `nexa-mark--${variant}`,
        staticMark && 'nexa-mark--static',
        className,
      )}
      aria-hidden
      data-testid={variant === 'compact' ? 'nexa-query-mark' : undefined}
    >
      <defs>
        <linearGradient
          id={armGrad}
          x1="430.07"
          y1="419.04"
          x2="430.07"
          y2="124.76"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0166aa" />
          <stop offset="0.49" stopColor="#0171b7" />
          <stop offset="1" stopColor="#0181cb" />
        </linearGradient>
        <linearGradient
          id={arrowGrad}
          x1="112.47"
          y1="163.43"
          x2="600.93"
          y2="163.43"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#061e3c" />
          <stop offset="0.21" stopColor="#05203f" />
          <stop offset="0.37" stopColor="#052749" />
          <stop offset="0.52" stopColor="#043359" />
          <stop offset="0.66" stopColor="#044371" />
          <stop offset="0.79" stopColor="#03598f" />
          <stop offset="0.92" stopColor="#0173b4" />
          <stop offset="1" stopColor="#0187d0" />
        </linearGradient>
      </defs>

      <circle className="nexa-mark__bloom" cx="310.78" cy="296.85" r="52" fill="#05b8ec" />

      <g className="nexa-mark__arms">
        <path
          className="nexa-mark__arm nexa-mark__arm--1"
          fill="#07213f"
          stroke="#07213f"
          strokeMiterlimit={10}
          strokeWidth={0.3}
          d="M478.47,437.89l-158.49-49.98-180.32,144.87,151-185.07c6.23,2.47,13.02,3.82,20.12,3.82,10.29,0,19.91-2.84,28.13-7.78l139.56,94.14Z"
        />
        <path
          className="nexa-mark__arm nexa-mark__arm--2"
          fill="#07213f"
          stroke="#07213f"
          strokeMiterlimit={10}
          strokeWidth={0.3}
          d="M126.42,518.69l95.91-204.58-101.28-133.14,136.35,104c-.85,3.82-1.3,7.8-1.3,11.88,0,13.32,4.76,25.53,12.68,35.01l-142.36,186.83Z"
        />
        <path
          className="nexa-mark__arm nexa-mark__arm--3"
          fill={`url(#${armGrad})`}
          stroke="#07213f"
          strokeMiterlimit={10}
          strokeWidth={0.3}
          d="M398.5,291.94l77.47,127.09-116.48-97.3c3.81-7.46,5.97-15.92,5.97-24.88,0-9.82-2.59-19.04-7.12-27,85.55-105.46,142.56-144.49,143.45-145.09-14.98,19.09-103.29,167.18-103.29,167.18Z"
        />
      </g>

      <g className="nexa-mark__arrow">
        <path
          fill={`url(#${arrowGrad})`}
          stroke="#07213f"
          strokeMiterlimit={10}
          strokeWidth={0.3}
          d="M525.83,127.81l5.77-27.76c-90.37,23.03-180.75,132.64-193.97,149.17-7.93-4.48-17.09-7.04-26.85-7.04-15.82,0-30.07,6.72-40.06,17.47l-158.25-105.1,202.06,53.82c140-117.82,214.77-120.44,214.77-120.44,0-1.38-11.31-20.71-11.31-20.71l82.94,15.67c-40.09,15.43-75.1,44.92-75.1,44.92Z"
        />
      </g>

      <circle
        className="nexa-mark__core"
        cx="310.78"
        cy="296.85"
        r="31.87"
        fill="#05b8ec"
        stroke="#07213f"
        strokeMiterlimit={10}
        strokeWidth={0.3}
      />
    </svg>
  );
}
